import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";

/**
 * Seamless background carousel — zero blanks, zero flicker.
 * All images are always in the DOM. GSAP crossfades between layers.
 * Ken Burns keeps the active slide alive while it waits.
 */
const Carousel = ({ images = [] }) => {
  const [active, setActive] = useState(0);
  const imgRefs = useRef([]);
  const dotsRef = useRef([]);
  const kenBurnsTween = useRef(null);
  const timerRef = useRef(null);
  const isAnimating = useRef(false);

  // ─── Ken Burns on whichever slide is active ───────────────────────────────
  const startKenBurns = (index) => {
    if (kenBurnsTween.current) kenBurnsTween.current.kill();
    const el = imgRefs.current[index];
    if (!el) return;

    const moves = [
      { xPercent: -4, yPercent: -3, scale: 1.12 },
      { xPercent:  4, yPercent: -2, scale: 1.10 },
      { xPercent: -3, yPercent:  4, scale: 1.08 },
      { xPercent:  3, yPercent:  3, scale: 1.11 },
    ];
    const move = moves[index % moves.length];

    gsap.set(el, { xPercent: 0, yPercent: 0, scale: 1.06 });
    kenBurnsTween.current = gsap.to(el, {
      ...move,
      duration: 9,
      ease: "none",
    });
  };

  // ─── Dot indicators ───────────────────────────────────────────────────────
  const syncDots = (index) => {
    dotsRef.current.forEach((dot, i) => {
      if (!dot) return;
      gsap.to(dot, {
        width:   i === index ? 28 : 8,
        opacity: i === index ? 1  : 0.35,
        duration: 0.45,
        ease: "power2.out",
      });
    });
  };

  // ─── Core transition ──────────────────────────────────────────────────────
  const transitionTo = (nextIndex) => {
    if (isAnimating.current) return;
    if (nextIndex === active) return;
    isAnimating.current = true;

    const currentEl = imgRefs.current[active];
    const nextEl    = imgRefs.current[nextIndex];

    // Bring next to front, reset its position before Ken Burns kicks in
    gsap.set(nextEl, { zIndex: 2, opacity: 0, scale: 1.06, xPercent: 0, yPercent: 0 });
    gsap.set(currentEl, { zIndex: 1 });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(currentEl, { zIndex: 0, opacity: 0 });
        gsap.set(nextEl,    { zIndex: 1 });
        setActive(nextIndex);
        isAnimating.current = false;
      },
    });

    // Fade + scale-settle on incoming
    tl.to(nextEl, {
      opacity: 1,
      scale:   1,
      duration: 1.4,
      ease: "power2.inOut",
    });

    // Start Ken Burns on new slide mid-transition so it's moving when fully visible
    tl.call(() => startKenBurns(nextIndex), [], 0.2);

    syncDots(nextIndex);
  };

  // ─── Auto-advance ─────────────────────────────────────────────────────────
  const scheduleNext = (fromIndex) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const next = (fromIndex + 1) % images.length;
      transitionTo(next);
    }, 5000);
  };

  useEffect(() => {
    scheduleNext(active);
    return () => clearTimeout(timerRef.current);
  }, [active]);

  // ─── Mount: set initial z-stack, start Ken Burns on slide 0 ──────────────
  useEffect(() => {
    imgRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.set(el, {
        opacity: i === 0 ? 1 : 0,
        zIndex:  i === 0 ? 1 : 0,
        scale: 1.06,
      });
    });
    startKenBurns(0);
    syncDots(0);
  }, [images.length]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="absolute inset-0 -z-20 overflow-hidden"
      style={{ backgroundColor: "#000" }}
    >
      {/* All slides always mounted — zero conditional rendering = zero blank frames */}
      {images.map((src, i) => (
        <img
          key={src + i}
          ref={(el) => (imgRefs.current[i] = el)}
          src={src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{
            willChange: "transform, opacity",
            transformOrigin: "center center",
            opacity: 0,
            zIndex: 0,
          }}
        />
      ))}

      {/* Permanent cinematic gradient — never removed, no flicker */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.04) 45%, rgba(0,0,0,0.52) 100%)",
          zIndex: 10,
          pointerEvents: "none",
        }}
      />

      {/* Dot indicators */}
      <div
        className="absolute bottom-5 left-1/2"
        style={{
          transform: "translateX(-50%)",
          display: "flex",
          gap: 6,
          alignItems: "center",
          zIndex: 20,
        }}
      >
        {images.map((_, i) => (
          <button
            key={i}
            ref={(el) => (dotsRef.current[i] = el)}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => {
              clearTimeout(timerRef.current);
              transitionTo(i);
            }}
            style={{
              width: 8,
              height: 8,
              borderRadius: 9999,
              backgroundColor: "#fff",
              opacity: 0.35,
              border: "none",
              padding: 0,
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;